import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import BuilderModal from "../../../Layouts/Modal/BuilderModal";
import PageInfoAndActionBar from "../../../Layouts/PageInfoAndActionBar";
import MaterialIcon from "../../../components/BuilderComponents/MaterialIcon";
import NotesAndTags from "../../../components/BuilderComponents/NotesAndTags";
import Input from "../../../components/FormComponents/Input";
import Textarea from "../../../components/FormComponents/Textarea";
import { useAppBlocksContext } from "../../../context/AppBlocksContext";
import { useAuth0Context } from "../../../context/Auth0Context";
import { useAxiosContext } from "../../../context/AxiosContext";
import { useTableContext } from "../../../context/TableContext";
import useZTheme from "../../../hooks/useZTheme";
import axiosInstance, { handleApiError } from "../../../services/axiosService";
import { elementConfig } from "../../../utils/Config/DefaultConfig/elementConfig";
import { toastError, toastSuccess } from "../../../utils/toastUtils";
import { getToolTipAttributes } from "../../../utils/tooltip";
import PositionScreenWithEdit from "../../FlexibleLayout/PositionScreenWithEdit";
import {
  findItemById,
  getOffset,
  getPositionedElementConfig,
  getScreenWidth,
  updateOnlyElementConfigInLayout
} from "../../FlexibleLayout/utils";
import DetailsviewElementsBar from "../DetailsViewRenderer/Desktop/DetailsviewElementsBar";
import DetailviewLayoutEditor from "../DetailsViewRenderer/Desktop/DetailviewLayoutEditor";
import { RVCAtalogViewForPostioned } from "../RecordsViewRenderer/Desktop/PositionedDesktopRecordview";
import ActivityCardEditActions from "./ActivityCardEditActions";

const ActivityCardRenderer = ({ applicationBlockData }) => {
  const tableRef = useRef({});
  const [dragFromOutside, setDragFromOutside] = useState(false);
  const { handleError } = useAxiosContext();
  const { getTableTypeData } = useTableContext();
  const { setSelectedAppBlock, layoutConfig, setLayoutConfig, setApplicationBlockData } = useAppBlocksContext();
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [draggedItem, setDraggedItem] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  useZTheme(applicationBlockData.device);
  const navigate = useNavigate();
  const isPositionBlock = applicationBlockData?.is_positioned_block;

  // const [screenLayoutInfo, setScreenLayoutInfo] = useState({
  //   showGrid: true,
  //   showTree: false,
  //   showTemplate: false,
  //   screenMode: "editable",
  //   scaleFactor: 1,
  //   gridSize: (() => {
  //     if (applicationBlockData.device === "desktop") {
  //       return 8;
  //     } else {
  //       return applicationBlockData.position_block_metadata?.grid_size || 4;
  //     }
  //   })()
  // });

  const [catalogWidth, setCatalogWidth] = useState(0);
  const [catalogLayoutConfig, setCatalogLayoutConfig] = useState([]);

  const initialCatalogPlaygroundState = {
    show: false,
    type: "",
    elementId: "",
    fieldId: "",
    fieldType: ""
  };
  const [isCatalogEditPlaygroundOpen, setIsCatalogEditPlaygroundOpen] = useState(initialCatalogPlaygroundState);

  const [showActivityLayout, setShowActivityLayout] = useState(true);

  const getNonPerformActivityLayoutConfig = () => {
    return applicationBlockData.data?.config?.position_config_for_non_activity || [];
  };

  const setNonPerformActivityLayoutConfig = (newLayoutConfig) => {
    if (typeof newLayoutConfig === "function") {
      setApplicationBlockData((prevAppBlockData) => {
        const currentLayoutConfig = prevAppBlockData.data?.config?.position_config_for_non_activity || [];
        const updatedLayoutConfig = newLayoutConfig(currentLayoutConfig);

        return {
          ...prevAppBlockData,
          data: {
            ...prevAppBlockData.data,
            config: {
              ...prevAppBlockData.data?.config,
              position_config_for_non_activity: updatedLayoutConfig
            }
          }
        };
      });
    } else {
      setApplicationBlockData({
        ...applicationBlockData,
        data: {
          ...applicationBlockData.data,
          config: {
            ...applicationBlockData.data?.config,
            position_config_for_non_activity: newLayoutConfig
          }
        }
      });
    }
  };

  const fetchCurrentSmData = async () => {
    try {
      const { tableObject } = await getTableTypeData(
        applicationBlockData.table_type,
        applicationBlockData.data.table_id
      );
      tableRef.current = { ...tableObject };
    } catch (error) {
      handleError(error.message);
      navigate("../");
      setSelectedAppBlock({ id: "", name: "", parentData: "", table_id: "", activity_id: "" });
    }
  };

  const handleDragStart = (e, name, id, type) => {
    setDragFromOutside(true);
    e.dataTransfer.setData("id", id);
    e.dataTransfer.setData("type", type);
    e.dataTransfer.setData("name", name);

    if (isPositionBlock) {
      type = type === "empty" ? "field" : type;
      let newConfig = getPositionedElementConfig(type, id);
      if (newConfig.type === "element") {
        if (type === "activitycard" || type === "activity-recordsview") {
          newConfig.config = {
            type,
            id
          };
        } else {
          newConfig.config = elementConfig[type === "tableview" ? "table" : type];
        }
      }
      const offset = getOffset(e);
      setDraggedItem(newConfig);
      setDragOffset(offset);
    }
  };

  const openCatalogPlayground = (type, elementId, fieldId, fieldType, cardWidth, layout) => {
    setIsCatalogEditPlaygroundOpen({
      show: true,
      type,
      elementId,
      fieldId,
      fieldType
    });
    setCatalogWidth(cardWidth);
    setCatalogLayoutConfig(layout || []);
  };

  const closeCatalogEditPlayground = () => {
    setIsCatalogEditPlaygroundOpen(initialCatalogPlaygroundState);
  };

  const handleClick = (item, info) => {
    if (info && info.type && info.type === "catalog_edit") {
      const actualItem = findItemById(layoutConfig, item.id);

      const catalog = actualItem?.config?.catalog ?? {};
      const cardsPerRow = catalog.cards_per_row ?? 1;
      const gapBetweenCards = parseInt(catalog.gap_between_cards ?? 18);

      const totalWidth = parseInt(actualItem.style.width) - (cardsPerRow - 1) * gapBetweenCards;
      const cardWidth = Math.floor(totalWidth / cardsPerRow);

      openCatalogPlayground(info.type, actualItem.id, info.field_id, info.field_type, cardWidth, catalog.layout || []);
    }
  };

  useEffect(() => {
    if (applicationBlockData.id) {
      fetchCurrentSmData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationBlockData.id]);

  useEffect(() => {
    if (!isCatalogEditPlaygroundOpen.show || !catalogLayoutConfig) return;

    const tableElement = findItemById(layoutConfig, isCatalogEditPlaygroundOpen.elementId);
    if (!tableElement) return;

    const fieldConfig = JSON.parse(JSON.stringify(tableElement.config)) || {};
    fieldConfig.catalog = fieldConfig.catalog || {};
    fieldConfig.catalog.layout = catalogLayoutConfig || [];

    const newLayoutConfig = updateOnlyElementConfigInLayout(
      layoutConfig,
      isCatalogEditPlaygroundOpen.elementId,
      fieldConfig
    );

    setLayoutConfig(newLayoutConfig);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogLayoutConfig, isCatalogEditPlaygroundOpen.show]);

  if (isCatalogEditPlaygroundOpen.show) {
    return (
      <RVCAtalogViewForPostioned
        width={catalogWidth}
        height={"100%"}
        layoutConfig={catalogLayoutConfig}
        setLayoutConfig={setCatalogLayoutConfig}
        device={applicationBlockData.device}
        tableId={applicationBlockData.data.table_id}
        tableType={applicationBlockData.table_type}
        elements={[applicationBlockData]}
        onClose={closeCatalogEditPlayground}
        targetFolderPath={[isCatalogEditPlaygroundOpen.fieldId]}
      />
    );
  }

  return (
    <div className="detailsView_outer_body">
      <div className="screen-center-wrapper">
        {/* <DetailviewInfoBar saveBlock={handleSaveApplicationBlock} cloneBlock={handleCloneApplicationBlock} /> */}
        <PageInfoAndActionBarForActivityCard />

        <div className="page-action-bar-top align_center">
          {isPositionBlock && (
            <div className="builder_form_icons_container pl_2 position_absolute left_50">
              <div
                className={`mode-option ${showActivityLayout ? "active" : ""}`}
                onClick={() => setShowActivityLayout(true)}
              >
                <MaterialIcon
                  name="clarify"
                  className="mode-options-icons"
                  {...getToolTipAttributes("Activity Card", "bottom")}
                />
              </div>
              <div
                className={`mode-option ${!showActivityLayout ? "active" : ""}`}
                onClick={() => setShowActivityLayout(false)}
              >
                <MaterialIcon
                  name="animated_images"
                  className="mode-options-icons"
                  {...getToolTipAttributes("Placeholder Card", "bottom")}
                />
              </div>
            </div>
          )}

          <ActivityCardEditActions />

          {/* <PositionScreenActionBar
            screenLayoutInfo={screenLayoutInfo}
            setScreenLayoutInfo={setScreenLayoutInfo}
            device={applicationBlockData.device}
          /> */}
        </div>

        <div className="position-screen-center-page">
          {isPositionBlock ? (
            <>
              <PositionScreenWithEdit
                key={showActivityLayout ? "activity-layout" : "non-activity-layout"}
                layoutConfig={showActivityLayout ? layoutConfig || [] : getNonPerformActivityLayoutConfig()}
                setLayoutConfig={showActivityLayout ? setLayoutConfig : setNonPerformActivityLayoutConfig}
                draggedItem={draggedItem}
                setDragOffset={setDragOffset}
                setDraggedItem={setDraggedItem}
                dragOffset={dragOffset}
                selectedItem={selectedItem}
                setSelectedItem={setSelectedItem}
                screenWidth={getScreenWidth({
                  device: applicationBlockData.device,
                  metadata: applicationBlockData.position_block_metadata
                })}
                tableId={applicationBlockData.data.table_id}
                tableType={applicationBlockData.table_type}
                device={applicationBlockData.device}
                buttonElements={[applicationBlockData]}
                targetFolderPath={
                  showActivityLayout
                    ? [applicationBlockData.data?.activity_id]
                    : [applicationBlockData.data?.activity_id, "global_fields"]
                }
                onClick={handleClick}
              />
            </>
          ) : (
            <DetailviewLayoutEditor
              dragFromOutside={dragFromOutside}
              setDragFromOutside={setDragFromOutside}
              tableRef={tableRef}
              device={applicationBlockData.device}
              tableID={applicationBlockData.data.table_id}
              tableType={applicationBlockData.table_type}
              activityID={applicationBlockData.data?.activity_id}
              elements={[applicationBlockData]}
              variables={applicationBlockData.data.variables}
              headerComponent={<ActivityCardEditActions />}
            />
          )}
        </div>
      </div>

      <DetailsviewElementsBar
        handleDragStart={handleDragStart}
        device={applicationBlockData.device}
        isPositionedScreen={applicationBlockData.is_positioned_block}
      />
    </div>
  );
};

const PageInfoAndActionBarForActivityCard = () => {
  const {
    applicationBlockData,
    layout,
    layoutData,
    nestedGridData,
    handleSaveApplicationBlock,
    lockData,
    setLockData,
    handleCloneApplicationBlock,
    setApplicationBlockData
  } = useAppBlocksContext();
  const { user } = useAuth0Context();

  const initialModalState = {
    isOpen: false,
    type: "",
    name: ""
  };
  const [modalState, setModalState] = useState(initialModalState);

  const openModal = (type, name) => {
    setModalState({ isOpen: true, type, name });
  };
  const closeModal = () => {
    setModalState(initialModalState);
  };

  const handletoggleLock = async () => {
    try {
      if (lockData && lockData.user_id) {
        if (lockData.user_id !== user.sub) {
          toastError("Unauthorized");
          return;
        }
        const { message } = await axiosInstance.delete(`/app/lock/${applicationBlockData.id}`);
        toastSuccess(message);
        setLockData({});
      } else {
        const userinfo = `${user.first_name} (${user.email})`;
        const lock = {
          type: "detailview",
          user_id: user.sub,
          user_info: userinfo,
          type_id: applicationBlockData.id,
          type_name: applicationBlockData.name
        };
        const { message } = await axiosInstance.post(`/app/lock`, lock);
        toastSuccess(message);
        setLockData(lock);
      }
    } catch (error) {
      handleApiError(error);
    }
  };
  const handleChange = (data) => {
    setApplicationBlockData({
      ...applicationBlockData,
      activitycard_settings: {
        ...applicationBlockData.activitycard_settings,
        ...data
      }
    });
  };
  return (
    <>
      <PageInfoAndActionBar
        pagePath={`Blocks/Detailsview/${applicationBlockData.name}`}
        currentConfig={[layout, nestedGridData.current, layoutData.current, applicationBlockData]}
        pageName={"applicationblock"}
        savePage={handleSaveApplicationBlock}
        clonePage={handleCloneApplicationBlock}
        toggleLock={handletoggleLock}
        isLocked={lockData?.user_id || null}
        notesPage={() => openModal("notes", "Notes and tags")}
        settingPage={() => openModal("settings", "Settings")}
      />

      <BuilderModal isOpen={modalState.isOpen} onClose={closeModal} modalName={modalState.name}>
        {modalState.type === "settings" && (
          <>
            <ActivityCardSettings
              blockSetting={{
                show_print: applicationBlockData.activitycard_settings?.show_print || false,
                print_custom_css: applicationBlockData.activitycard_settings?.print_custom_css || ""
              }}
              handleChange={handleChange}
            />
          </>
        )}
        {modalState.type === "notes" && (
          <NotesAndTags
            notesValue={applicationBlockData.description || ""}
            tagValue={applicationBlockData.info_tag || []}
            onChange={handleChange}
          />
        )}
      </BuilderModal>
    </>
  );
};

const ActivityCardSettings = ({ blockSetting, handleChange }) => {
  const handleInputChange = (event) => {
    const { type, name, value, checked } = event.target;
    const newValue = type === "checkbox" ? checked : value;

    handleChange({
      ...blockSetting,
      [name]: newValue
    });
  };

  return (
    <div className="builder_form">
      <div className="center_container ">
        <Input type="checkbox" name="show_print" checked={blockSetting.show_print} onChange={handleInputChange} />
        <label className="builder_label mb_0">Show Print</label>
      </div>
      <div className="builder_form">
        <Textarea
          label="Print Css"
          value={blockSetting.print_custom_css}
          name="print_custom_css"
          onChange={handleInputChange}
        />
      </div>
    </div>
  );
};

export default ActivityCardRenderer;
